/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v4-mini";
import { remap as remap$ } from "../../lib/primitives.js";
import { safeParse } from "../../lib/schemas.js";
import * as openEnums from "../../types/enums.js";
import { OpenEnum } from "../../types/enums.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";

/**
 * Whether the skill has no distribution target, one target, or conflicting targets.
 */
export const TargetState = {
  NotDistributed: "not_distributed",
  Single: "single",
  Ambiguous: "ambiguous",
} as const;
/**
 * Whether the skill has no distribution target, one target, or conflicting targets.
 */
export type TargetState = OpenEnum<typeof TargetState>;

/**
 * Active-machine convergence against the skill's plugin distribution target.
 */
export type SkillDrift = {
  /**
   * Machines that activated the skill during the window.
   */
  activeMachines: number;
  /**
   * Active machines whose latest attributed activation used another version.
   */
  driftedMachines: number;
  /**
   * Active machines without a version or without one unambiguous target.
   */
  indeterminateMachines: number;
  /**
   * Active machines whose latest activation used the target version.
   */
  onTargetMachines: number;
  /**
   * Whether the skill has no distribution target, one target, or conflicting targets.
   */
  targetState: TargetState;
  /**
   * Distinct versions targeted by active plugin distributions.
   */
  targetVersionIds: Array<string>;
  /**
   * End of the active-machine window.
   */
  windowEnd: Date;
  /**
   * Start of the active-machine window.
   */
  windowStart: Date;
};

/** @internal */
export const TargetState$inboundSchema: z.ZodMiniType<TargetState, unknown> =
  openEnums.inboundSchema(TargetState);

/** @internal */
export const SkillDrift$inboundSchema: z.ZodMiniType<SkillDrift, unknown> = z
  .pipe(
    z.object({
      active_machines: z.int(),
      drifted_machines: z.int(),
      indeterminate_machines: z.int(),
      on_target_machines: z.int(),
      target_state: TargetState$inboundSchema,
      target_version_ids: z.array(z.string()),
      window_end: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
      window_start: z.pipe(
        z.iso.datetime({ offset: true }),
        z.transform(v => new Date(v)),
      ),
    }),
    z.transform((v) => {
      return remap$(v, {
        "active_machines": "activeMachines",
        "drifted_machines": "driftedMachines",
        "indeterminate_machines": "indeterminateMachines",
        "on_target_machines": "onTargetMachines",
        "target_state": "targetState",
        "target_version_ids": "targetVersionIds",
        "window_end": "windowEnd",
        "window_start": "windowStart",
      });
    }),
  );

export function skillDriftFromJSON(
  jsonString: string,
): SafeParseResult<SkillDrift, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => SkillDrift$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'SkillDrift' from JSON`,
  );
}
